import { FC, useCallback, useContext, useMemo } from "react";
import i18n from "@/i18n";
import {
  Filter,
  RefSchemaItem,
  RowWithEmbeddedMetadata,
} from "@data-root/types";
import {
  SchemaItemProps,
  useAppContext,
  useClientContext,
  useField,
  CustomButton,
  View,
  useRows,
  useFormulaWithDependencies,
} from "@data-root/react";
import { Button, Space, Typography } from "antd";
import { RefInputContext } from ".";
import { Remarks } from "../../remarks";
import { PlusOutlined } from "@ant-design/icons";

const { Title } = Typography;

export const RefInputInlinePresentation: FC<SchemaItemProps<RefSchemaItem>> = ({
  item,
}) => {
  const { core } = useClientContext();
  const { appSpecifier, styles } = useAppContext();
  const {
    loaded,
    displayName,
    editable,
    value: _value,
    setValue: _setValue,
    remarks,
  } = useField<string>(item);

  const { onAdd, refTable, pinned, ready } = useContext(RefInputContext)!;

  const { value: inheritedFilters } = useFormulaWithDependencies<Filter[]>(
    core,
    item.filter
  );

  const filters = useMemo(() => {
    const filters = [];
    if (inheritedFilters) filters.push(...inheritedFilters);
    return filters;
  }, [inheritedFilters]);

  const {
    loaded: refRows_loaded,
    rows: refRows,
    fetch,
  } = useRows(core, appSpecifier, item.ref, filters);

  const handleSetValue = useCallback(
    (row: RowWithEmbeddedMetadata) => {
      if (!editable || !loaded) return;
      const displayValue = row[`$${refTable.label}`] as string;
      _setValue(row.id, displayValue);
    },
    [_setValue, editable, loaded, refTable.label]
  );

  if (!ready || !refRows_loaded) return null;

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{displayName}</Title>

      <Space wrap>
        {pinned?.map((option) => (
          <Button
            key={option.id}
            onClick={() =>
              handleSetValue({
                id: option.id,
                [`$${refTable.label}`]: option.name,
              } as any)
            }
            type={option.id === _value ? "primary" : "default"}
          >
            {option.name}
          </Button>
        ))}

        {refRows.map((row) => (
          <Button
            key={row.id}
            onClick={() => handleSetValue(row)}
            type={row.id === _value ? "primary" : "default"}
          >
            {String(row[`$${refTable.label}`])}
          </Button>
        ))}

        <CustomButton
          type="link"
          onClick={fetch}
          icon={<PlusOutlined />}
          style={{
            color: styles.primaryColor,
            opacity: 0.5,
          }}
        >
          {i18n.t("load_more")}
        </CustomButton>
      </Space>

      <Remarks texts={remarks} />
    </View>
  );
};
